import React, { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '@/app/store/authStore';
import { useFeedStore } from '@/app/store/feedStore';
import { api } from '@/shared/services/api';
import { ExportPreviewDto, ExportPreviewItemDto } from '@/shared/types/api';
import {
  DownloadIcon,
  FileTextIcon,
  FileIcon,
  FilterIcon,
  AlertCircleIcon,
  BarChart3Icon,
  CheckCircle2Icon,
  CircleIcon,
  StarIcon,
  LockIcon,
} from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { ClassificationModal } from './ClassificationModal';
import { classifyFeedItem, ClassificationFormData } from './classificationApi';
import { cn } from '@/shared/utils/cn';
import { Spinner } from '@/shared/components/Spinner';
import { EmptyState } from '@/shared/components/EmptyState';
import { Badge } from '@/shared/components/Badge';
import { downloadBlob } from '@/shared/utils/download';
import { formatDate } from '@/shared/utils/date';

const MAX_RECORDS_LIMIT = 1000;
const DEFAULT_MAX_RECORDS = 500;

const exportSchema = z.object({
  format: z.enum(['pdf', 'excel', 'excel-advanced']),
  productType: z.string().optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  includeRead: z.boolean(),
  includeUnread: z.boolean(),
  includeStarred: z.boolean(),
  maxRecords: z.coerce
    .number({ error: 'Enter a number' })
    .int('Must be a whole number')
    .min(1, 'Must be at least 1')
    .max(MAX_RECORDS_LIMIT, `Cannot exceed ${MAX_RECORDS_LIMIT}`),
}).refine(data => data.includeRead || data.includeUnread, {
  message: "Select at least one read status filter",
  path: ["includeRead"]
});

type ExportFormInput = z.input<typeof exportSchema>;
type ExportFormData = z.output<typeof exportSchema>;

const FORMAT_MAP: Record<ExportFormData['format'], string> = {
  pdf: 'Pdf',
  excel: 'Excel',
  'excel-advanced': 'ExcelAdvanced',
};

const FORMAT_OPTIONS = [
  { value: 'pdf', label: 'PDF', icon: FileTextIcon, description: 'Printable report' },
  { value: 'excel', label: 'Excel Basic', icon: FileIcon, description: 'Simple spreadsheet' },
  { value: 'excel-advanced', label: 'Excel Advanced', icon: BarChart3Icon, description: '10 sheets: charts, risk matrix & analytics' },
] as const;

interface ClassificationData {
  complexity?: string;
  impact?: string;
  tags?: string[];
}

interface ExportRequest {
  format: string;
  productType?: string;
  startDate?: string;
  endDate?: string;
  isRead?: boolean;
  isStarred?: boolean;
  maxRecords?: number;
}

// Builds the export/preview request payload from the form state. Shared by the
// live preview and the actual export so both apply an identical filter set.
const buildExportRequest = (data: ExportFormData): ExportRequest => {
  const exportRequest: ExportRequest = {
    format: FORMAT_MAP[data.format],
  };

  if (data.productType && data.productType !== '') {
    exportRequest.productType = data.productType;
  }

  if (data.startDate) {
    exportRequest.startDate = data.startDate;
  }

  if (data.endDate) {
    exportRequest.endDate = data.endDate;
  }

  if (!data.includeRead && data.includeUnread) {
    exportRequest.isRead = false;
  } else if (data.includeRead && !data.includeUnread) {
    exportRequest.isRead = true;
  }

  if (data.includeStarred) {
    exportRequest.isStarred = true;
  }

  exportRequest.maxRecords = data.maxRecords;

  return exportRequest;
};

export const Export: React.FC = () => {
  const { user } = useAuthStore();
  const { productTypes, loadProductTypes } = useFeedStore();
  const [exportLoading, setExportLoading] = useState(false);
  const [previewData, setPreviewData] = useState<ExportPreviewItemDto[]>([]);
  const [estimatedCount, setEstimatedCount] = useState<number | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [classificationModal, setClassificationModal] = useState<{
    isOpen: boolean;
    feedItemId?: number;
    currentData?: ClassificationData;
  }>({ isOpen: false });

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ExportFormInput, unknown, ExportFormData>({
    resolver: zodResolver(exportSchema),
    defaultValues: {
      format: 'excel-advanced',
      includeRead: true,
      includeUnread: true,
      includeStarred: false,
      maxRecords: DEFAULT_MAX_RECORDS,
    },
  });

  const watchedValues = watch();

  useEffect(() => {
    loadProductTypes();
  }, [loadProductTypes]);

  // Authoritative count: ask the server how many items the current filters would export.
  const {
    format: watchedFormat,
    productType: watchedProductType,
    startDate: watchedStartDate,
    endDate: watchedEndDate,
    includeRead: watchedIncludeRead,
    includeUnread: watchedIncludeUnread,
    includeStarred: watchedIncludeStarred,
    maxRecords: watchedMaxRecords,
  } = watchedValues;

  useEffect(() => {
    if (!user) return;

    // Both read-status boxes off is an invalid filter (see schema); skip the request.
    if (!watchedIncludeRead && !watchedIncludeUnread) {
      setEstimatedCount(0);
      return;
    }

    // While the field is being edited it may be empty/NaN/out-of-range; fall back
    // to a sane bounded value so the preview count stays meaningful.
    const parsedMaxRecords = Number(watchedMaxRecords);
    const safeMaxRecords = Number.isFinite(parsedMaxRecords) && parsedMaxRecords > 0
      ? Math.min(parsedMaxRecords, MAX_RECORDS_LIMIT)
      : DEFAULT_MAX_RECORDS;

    let cancelled = false;
    setPreviewLoading(true);

    const timer = setTimeout(async () => {
      try {
        const request = buildExportRequest({
          format: watchedFormat,
          productType: watchedProductType,
          startDate: watchedStartDate,
          endDate: watchedEndDate,
          includeRead: watchedIncludeRead,
          includeUnread: watchedIncludeUnread,
          includeStarred: watchedIncludeStarred,
          maxRecords: safeMaxRecords,
        });

        const response = await api.post<ExportPreviewDto>('/export/preview', request);
        if (!cancelled) {
          setEstimatedCount(response.data.estimatedItemCount);
          // SC-255: the cards come from the same filtered query as the count, so they honour
          // the date range, product type, search term and Max Records.
          setPreviewData(response.data.sampleItems ?? []);
        }
      } catch (error) {
        console.error('Export preview failed:', error);
        if (!cancelled) {
          setEstimatedCount(null);
          setPreviewData([]);
        }
      } finally {
        if (!cancelled) {
          setPreviewLoading(false);
        }
      }
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    user,
    watchedFormat,
    watchedProductType,
    watchedStartDate,
    watchedEndDate,
    watchedIncludeRead,
    watchedIncludeUnread,
    watchedIncludeStarred,
    watchedMaxRecords,
  ]);

  const downloadFile = useCallback((blob: Blob, filename: string) => {
    try {
      downloadBlob(blob, filename);
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Failed to download file. Please try again.');
    }
  }, []);


  const handleSaveClassification = async (data: ClassificationFormData) => {
    if (!classificationModal.feedItemId) return;
    
    await classifyFeedItem(classificationModal.feedItemId, data);
  };

  const onSubmit = async (data: ExportFormData) => {
    if (!user) {
      toast.error('Please log in to export data');
      return;
    }

    setExportLoading(true);

    try {
      // Build export request payload (identical filters used for preview + export)
      const exportRequest = buildExportRequest(data);

      // Choose endpoint based on format
      const endpoint = data.format === 'pdf' ? '/export/pdf' : '/export/excel';

      const response = await api.post(endpoint, exportRequest, {
        responseType: 'blob', // Important for file download
      });

      // Get filename from response headers or generate one
      const contentDisposition = response.headers['content-disposition'];
      let filename = `fenergo_export_${format(new Date(), 'yyyyMMdd_HHmmss')}`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      } else {
        // Generate filename based on format
        const extension = data.format === 'pdf' ? 'pdf' : 'xlsx';
        const formatSuffix = data.format === 'excel-advanced' ? '_advanced' : '';
        filename = `fenergo_${data.format.replace('-', '_')}${formatSuffix}_${format(new Date(), 'yyyyMMdd_HHmmss')}.${extension}`;
      }

      const blob = response.data as Blob;
      downloadFile(blob, filename);

      // Show success message with export details
      const formatLabel = FORMAT_OPTIONS.find(opt => opt.value === data.format)?.label || data.format;
      toast.success(`${formatLabel} export completed successfully!`);

    } catch (error) {
      console.error('Export error:', error);
      toast.error(error instanceof Error ? error.message : 'Export failed. Please try again.');
    } finally {
      setExportLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="animate-fade-in">
        <EmptyState
          icon={LockIcon}
          title="Sign in required"
          description="Please log in to access export functionality."
        />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">Export RSS Data</h1>
        <p className="text-muted-foreground">Export your RSS feed data in various formats</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Export Form */}
        <div className="card">
          <div className="p-4 border-b border-border">
            <h3 className="text-lg font-semibold text-foreground">Export Settings</h3>
            <p className="text-sm text-muted-foreground">Configure your export preferences</p>
          </div>
          <div className="p-4">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* Export Format */}
              <div>
                <label className="label mb-2">Export Format</label>
                <div className="grid grid-cols-2 gap-3">
                  {FORMAT_OPTIONS.map((formatOption) => (
                    <label key={formatOption.value} className="relative">
                      <input
                        {...register('format')}
                        type="radio"
                        value={formatOption.value}
                        className="sr-only peer"
                      />
                      <div className="flex h-full flex-col items-center rounded-lg border-2 border-border bg-card p-3 text-center cursor-pointer transition-colors hover:bg-muted peer-checked:border-primary peer-checked:bg-primary/10 peer-focus-visible:ring-2 peer-focus-visible:ring-primary">
                        <formatOption.icon className="h-6 w-6 text-muted-foreground peer-checked:text-primary" aria-hidden="true" />
                        <span className="text-sm font-semibold text-foreground mt-1">{formatOption.label}</span>
                        <span className="text-xs text-muted-foreground">{formatOption.description}</span>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Filters */}
              <div className="space-y-4">
                <h4 className="text-sm font-semibold text-foreground flex items-center">
                  <FilterIcon className="h-4 w-4 mr-2 text-muted-foreground" aria-hidden="true" />
                  Filters
                </h4>

                {/* Product Type */}
                <div>
                  <label htmlFor="productType" className="label">
                    Product Type
                  </label>
                  <select
                    {...register('productType')}
                    className="input mt-1"
                  >
                    <option value="">All Product Types</option>
                    {productTypes.map((pt) => (
                      <option key={pt.value} value={pt.name}>
                        {pt.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Date Range */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label htmlFor="startDate" className="label">
                      Start Date
                    </label>
                    <input
                      {...register('startDate')}
                      type="date"
                      className="input mt-1"
                    />
                  </div>
                  <div>
                    <label htmlFor="endDate" className="label">
                      End Date
                    </label>
                    <input
                      {...register('endDate')}
                      type="date"
                      className="input mt-1"
                    />
                  </div>
                </div>

                {/* Max Records */}
                <div>
                  <label htmlFor="maxRecords" className="label">
                    Max Records
                  </label>
                  <input
                    id="maxRecords"
                    {...register('maxRecords', { valueAsNumber: true })}
                    type="number"
                    min={1}
                    max={MAX_RECORDS_LIMIT}
                    step={1}
                    className="input mt-1"
                  />
                  <p className="mt-1 text-xs text-muted-foreground">
                    Number of items to export (1&ndash;{MAX_RECORDS_LIMIT}).
                  </p>
                  {errors.maxRecords && (
                    <p className="mt-1 text-sm text-danger">{errors.maxRecords.message}</p>
                  )}
                </div>

                {/* Status Filters */}
                <div>
                  <label className="label mb-2">Include Items</label>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        {...register('includeRead')}
                        type="checkbox"
                        className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                      />
                      <span className="ml-2 text-sm text-foreground">Read items</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        {...register('includeUnread')}
                        type="checkbox"
                        className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                      />
                      <span className="ml-2 text-sm text-foreground">Unread items</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        {...register('includeStarred')}
                        type="checkbox"
                        className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                      />
                      <span className="ml-2 text-sm text-foreground">Only starred items</span>
                    </label>
                  </div>
                  {errors.includeRead && (
                    <p className="mt-1 text-sm text-danger">{errors.includeRead.message}</p>
                  )}
                </div>
              </div>

              {/* Export Button */}
              <button
                type="submit"
                disabled={exportLoading}
                className="btn-primary w-full flex items-center justify-center space-x-2"
              >
                {exportLoading ? (
                  <Spinner />
                ) : (
                  <DownloadIcon className="h-4 w-4" aria-hidden="true" />
                )}
                <span>{exportLoading ? 'Exporting...' : 'Export Data'}</span>
              </button>
            </form>
          </div>
        </div>

        {/* Preview */}
        <div className="card">
          <div className="p-4 border-b border-border">
            <h3 className="text-lg font-semibold text-foreground">Preview</h3>
            <p className="text-sm text-muted-foreground">Sample of items that will be exported</p>
          </div>
          <div className="p-4">
            {previewLoading && estimatedCount === null ? (
              <div className="flex items-center justify-center py-8">
                <Spinner className="h-6 w-6 text-muted-foreground" />
                <span className="ml-2 text-muted-foreground">Loading preview...</span>
              </div>
            ) : previewData.length > 0 || (estimatedCount ?? 0) > 0 ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">
                    Showing {previewData.length} sample
                    {previewLoading || estimatedCount === null
                      ? ''
                      : ` of ${estimatedCount} item${estimatedCount === 1 ? '' : 's'} to export`}
                  </span>
                  {previewLoading ? (
                    <Spinner className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    estimatedCount !== null && estimatedCount > previewData.length && (
                      <span className="text-primary font-medium">+{estimatedCount - previewData.length} more items</span>
                    )
                  )}
                </div>
                {previewData.map((item) => (
                  <div key={item.id} className="p-3 border border-border rounded-lg bg-card">
                    <div className="flex items-start justify-between">
                      <div className="min-w-0 flex-1">
                        <h5 className={cn('text-sm text-foreground truncate', item.isRead ? 'font-medium' : 'font-semibold')}>{item.title}</h5>
                        <p className="text-xs text-muted-foreground mt-1">{item.feedName}</p>
                        <div className="flex items-center gap-2 mt-1.5">
                          <span className="text-xs text-muted-foreground">
                            {formatDate(item.publishedDate)}
                          </span>
                          {item.isRead ? (
                            <Badge variant="success">
                              <CheckCircle2Icon className="h-3 w-3" aria-hidden="true" />
                              Read
                            </Badge>
                          ) : (
                            <Badge variant="warning">
                              <CircleIcon className="h-3 w-3" aria-hidden="true" />
                              Unread
                            </Badge>
                          )}
                          {item.isStarred && (
                            <Badge variant="brand">
                              <StarIcon className="h-3 w-3 fill-current" aria-hidden="true" />
                              Starred
                            </Badge>
                          )}
                        </div>
                        {/* SC-423: the assignment columns the export carries */}
                        {item.assignedUsers && item.assignedUsers.length > 0 && (
                          <dl className="mt-1.5 space-y-0.5 text-xs text-muted-foreground">
                            <div className="flex gap-1">
                              <dt className="font-medium text-foreground/80">Assigned:</dt>
                              <dd>{item.assignedUsers.join(', ')}</dd>
                            </div>
                            {item.reviewByDate && (
                              <div className="flex gap-1">
                                <dt className="font-medium text-foreground/80">Review by:</dt>
                                {/* A calendar day: shown as stored, never shifted through a timezone. */}
                                <dd className="tabular-nums">{item.reviewByDate}</dd>
                              </div>
                            )}
                            {item.assignmentNotes && (
                              <div className="flex gap-1">
                                <dt className="font-medium text-foreground/80">Notes:</dt>
                                <dd className="line-clamp-2">{item.assignmentNotes}</dd>
                              </div>
                            )}
                          </dl>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState
                icon={AlertCircleIcon}
                title="No items match the current filters"
                description="Adjust your filters to see items."
              />
            )}
          </div>
        </div>
      </div>

      {/* Classification Modal */}
      {classificationModal.isOpen && (
        <ClassificationModal
          isOpen={classificationModal.isOpen}
          onClose={() => setClassificationModal({ isOpen: false })}
          onSave={handleSaveClassification}
          feedItemId={classificationModal.feedItemId!}
          currentComplexity={classificationModal.currentData?.complexity}
          currentImpact={classificationModal.currentData?.impact}
          currentTags={classificationModal.currentData?.tags}
        />
      )}
    </div>
  );
};
